'use client';

import { type ReactNode, useState } from 'react';

import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { ChatRoom } from '../../messaging/components/ChatRoom';
import { ChatSessionType } from '../../messaging/types/ChatSessionType';
import { isDmPermitted } from '../../subscription/functions/isDmPermitted';
import { useBioUserAccess } from '../contexts/user-access/useBioUserAccess';
import { NeedsLoginView } from './components/NeedsLoginView';
import { SubscribeGateView } from './components/SubscribeGateView';
import { dmSubscribeHref } from './functions/dmSubscribeHref';

interface Props {
  /** Creator user id == the DM peer / target == profile.id. */
  creatorId: string;
  creatorName: string;
  creatorAvatar?: string;
  dmMinimumSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  bioContext: BioContext;
  /** Leaves the chat (modal → router.back; standalone → back to bio). */
  onClose?: () => void;
}

/** Frame shared by the non-chat gate states: the chat backdrop, a top-left back
 *  affordance (the chat states have no ChatRoom header of their own), and the
 *  centered gate content. */
function GateShell({
  onClose,
  children,
}: {
  onClose?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col bg-[#121402]">
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Back"
          className="absolute left-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
      ) : null}
      <div className="flex flex-1 items-center justify-center p-6">
        {children}
      </div>
    </div>
  );
}

/**
 * Enforces the DM gate at the `/chat` destination — parity with the Message Me
 * button island, for direct URL opens. Reads the signed-in viewer + their
 * standing to this creator and renders one of:
 *
 *   - resolving → a spinner
 *   - signed out → {@link NeedsLoginView}
 *   - the creator themselves → a "can't message yourself" note (no self-DM)
 *   - a sufficiently-subscribed viewer → the live {@link ChatRoom}
 *   - under-subscribed → {@link SubscribeGateView}
 */
export function DmGate({
  creatorId,
  creatorName,
  creatorAvatar,
  dmMinimumSubscriptionLevel,
  bioContext,
  onClose,
}: Props) {
  const currentUserId = useCurrentUserId();
  const { extendedSubscriptionLevel, loading } = useBioUserAccess();
  const router = useRouter();
  // Which thread is shown — the 1:1 private DM or the creator's group broadcast.
  const [sessionType, setSessionType] = useState<ChatSessionType>(
    ChatSessionType.Private,
  );

  if (currentUserId === undefined || loading) {
    return (
      <GateShell onClose={onClose}>
        <Loader2 className="h-6 w-6 animate-spin text-white/50" aria-hidden />
      </GateShell>
    );
  }

  if (currentUserId === null) {
    return (
      <GateShell onClose={onClose}>
        <NeedsLoginView
          bioContext={bioContext}
          creatorName={creatorName}
          creatorAvatar={creatorAvatar}
        />
      </GateShell>
    );
  }

  // The creator viewing their own chat can't DM themselves.
  if (currentUserId === creatorId) {
    return (
      <GateShell onClose={onClose}>
        <p className="text-sm text-white/70">
          You can&apos;t message yourself.
        </p>
      </GateShell>
    );
  }

  if (!isDmPermitted(dmMinimumSubscriptionLevel, extendedSubscriptionLevel)) {
    return (
      <GateShell onClose={onClose}>
        <SubscribeGateView
          bioContext={bioContext}
          creatorName={creatorName}
          creatorAvatar={creatorAvatar}
          dmMinimumSubscriptionLevel={dmMinimumSubscriptionLevel}
        />
      </GateShell>
    );
  }

  return (
    <ChatRoom
      sessionType={sessionType}
      onSessionTypeChange={setSessionType}
      targetUserId={creatorId}
      targetUser={{ name: creatorName, avatar: creatorAvatar }}
      transient
      backButton
      bioContext={bioContext}
      // The signed-in fan is messaging the creator — the buyer shares their
      // own purchases from this creator.
      senderRole="buyer"
      onBack={onClose}
      onSubscribeRequired={minLevel =>
        router.push(dmSubscribeHref(bioContext, minLevel))
      }
    />
  );
}
